import type React from 'react';
import {
  BookOpen,
  ClipboardList,
  FileSearch,
  Gauge,
  KeyRound,
  ListChecks,
  Palette,
  Building2,
  Scale,
  Settings,
  UserCheck,
  Users,
  Users as UsersIcon,
  Stethoscope,
} from 'lucide-react';

export interface SubMenuItem {
  name: string;
  href: string;
  permission: string | string[];
  subItems?: SubMenuItem[];
}

export interface MenuItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permission: string | string[];
  subItems?: SubMenuItem[];
}

export interface MenuGroup {
  name: string;
  flat?: boolean;
  items: MenuItem[];
}

export const menuGroups: MenuGroup[] = [
  {
    name: 'Cadastros',
    items: [
      { name: 'Clientes', href: '/clientes', icon: Users, permission: 'Clientes' },
      { name: 'Funcionários', href: '/funcionarios', icon: UserCheck, permission: 'Funcionários' },
      { name: 'Usuários', href: '/usuarios', icon: UsersIcon, permission: 'Usuários' },
      { name: 'Agendamento', href: '/layout/agendamento', icon: Settings, permission: ['Agendamento', 'Agenda Diversos'] },
      {
        name: 'Layout e aparência',
        href: '#',
        icon: Palette,
        permission: 'Layout',
        subItems: [
          { name: 'Layout', href: '/layout', permission: 'Layout' },
          { name: 'Configurações', href: '/configuracoes', permission: 'Layout' },
        ],
      },
      {
        name: 'IA',
        href: '#',
        icon: Settings,
        permission: 'IA - Prompts',
        subItems: [{ name: 'Prompts', href: '/configuracoes/ia/prompts', permission: 'IA - Prompts' }],
      },
    ],
  },
  {
    name: 'Tributação',
    flat: true,
    items: [
      { name: 'Passo a passo', href: '/tributacao/passo-a-passo', icon: ListChecks, permission: ['Tributação - Confronto Fiscal', 'Tributação - Perfil Fiscal', 'Tributação - SPED', 'Tributação - Tokens API', 'Tributação'] },
      { name: 'Painel fiscal', href: '/tributacao/painel', icon: Gauge, permission: ['Tributação - Confronto Fiscal', 'Tributação - Perfil Fiscal', 'Tributação - SPED', 'Tributação - Tokens API', 'Tributação'] },
      {
        name: 'Cadastros',
        href: '#',
        icon: Scale,
        permission: ['Tributação - NCM ICMS', 'Tributação - Perfis'],
        subItems: [
          { name: 'NCM × IPI', href: '/tributacao/ncm-ipi', permission: ['Tributação - NCM ICMS'] },
          { name: 'ICMS Regras UF', href: '/tributacao/icms-uf', permission: ['Tributação - NCM ICMS'] },
          { name: 'Ex tarifário', href: '/tributacao/ex-tarifario', permission: ['Tributação - NCM ICMS'] },
          { name: 'Código de prestação de serviço', href: '/tributacao/codigo-prestacao-servico', permission: ['Tributação - Perfis'] },
          { name: 'ST', href: '/tributacao/st', permission: ['Tributação - Perfis'] },
          { name: 'FECP ICMS', href: '/tributacao/fecp-icms', permission: ['Tributação - Perfis'] },
          { name: 'FCP ST', href: '/tributacao/fcp-st', permission: ['Tributação - Perfis'] },
        ],
      },
      {
        name: 'Base de conhecimento',
        href: '#',
        icon: BookOpen,
        permission: ['Tributação - Base de Conhecimento'],
        subItems: [
          { name: 'Fontes e coletas', href: '/tributacao/base-conhecimento/fontes', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Novidades das fontes', href: '/tributacao/base-conhecimento/novidades', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Tributos', href: '/tributacao/base-conhecimento/tributos', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Regras do confronto', href: '/tributacao/base-conhecimento/regras', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'CFOP', href: '/tributacao/base-conhecimento/cfop', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Natureza de operação', href: '/tributacao/base-conhecimento/natureza-operacao', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'CST', href: '/tributacao/base-conhecimento/cst', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'IBS/CBS · cClassTrib', href: '/tributacao/base-conhecimento/cclasstrib', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'IBS/CBS · cIndOp', href: '/tributacao/base-conhecimento/cindop', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'NCM', href: '/tributacao/base-conhecimento/ncm', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'CEST', href: '/tributacao/base-conhecimento/cest', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'ICMS-ST / MVA', href: '/tributacao/base-conhecimento/icms-st', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Alíquotas e FCP', href: '/tributacao/base-conhecimento/aliquotas', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'FCP por UF e NCM', href: '/tributacao/base-conhecimento/fcp', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'UF: alíquota e benefícios', href: '/tributacao/base-conhecimento/uf-beneficios', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Regimes especiais', href: '/tributacao/base-conhecimento/regimes-especiais', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'cBenef por UF', href: '/tributacao/base-conhecimento/cbenef', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'TIPI', href: '/tributacao/base-conhecimento/tipi', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Cadastro de NCM × TIPI', href: '/tributacao/base-conhecimento/cruzamento-ncm', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Ex-tarifário', href: '/tributacao/base-conhecimento/ex-tarifario', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Produtos ANP', href: '/tributacao/base-conhecimento/anp', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'NCM × uTrib', href: '/tributacao/base-conhecimento/utrib', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'CNAE', href: '/tributacao/base-conhecimento/cnae', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'NBS', href: '/tributacao/base-conhecimento/nbs', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Serviços da NFS-e', href: '/tributacao/base-conhecimento/servicos-nfse', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'PTAX', href: '/tributacao/base-conhecimento/ptax', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Tabelas do SPED', href: '/tributacao/base-conhecimento/sped', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Normas', href: '/tributacao/base-conhecimento/normas', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Convênios e protocolos', href: '/tributacao/base-conhecimento/convenios', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Simples Nacional', href: '/tributacao/base-conhecimento/simples-nacional', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'ISS por município', href: '/tributacao/base-conhecimento/iss-municipio', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Localidades (IBGE)', href: '/tributacao/base-conhecimento/localidades', permission: ['Tributação - Base de Conhecimento'] },
          { name: 'Domínios da NF-e', href: '/tributacao/base-conhecimento/dominios-nfe', permission: ['Tributação - Base de Conhecimento'] },
        ],
      },
      {
        name: 'Perfis',
        href: '#',
        icon: Scale,
        permission: 'Tributação - Perfis',
        subItems: [
          { name: 'Perfil de produto', href: '/tributacao/produtos', permission: 'Tributação - Perfis' },
          { name: 'Perfil de participante', href: '/tributacao/perfil-participante', permission: 'Tributação - Perfis' },
          { name: 'Perfil origem × destino', href: '/tributacao/perfil-origem-destino', permission: 'Tributação - Perfis' },
          { name: 'Perfil de operação', href: '/tributacao/regra-calculo-doc-fiscal/perfil-operacao', permission: ['Tributação - Regras Cálculo', 'Tributação - Perfis'] },
        ],
      },
      {
        name: 'Regras de estoque/custo',
        href: '#',
        icon: Scale,
        permission: 'Tributação',
        subItems: [{ name: 'Regra de custo', href: '/tributacao/regra-custo', permission: 'Tributação' }],
      },
      {
        name: 'Regra de cálculo',
        href: '#',
        icon: Scale,
        permission: 'Tributação - Regras Cálculo',
        subItems: [
          { name: 'Regra base de cálculo', href: '/tributacao/regra-base-calculo', permission: 'Tributação - Regras Cálculo' },
          { name: 'Regra de alíquota', href: '/tributacao/regra-aliquota', permission: 'Tributação - Regras Cálculo' },
          { name: 'Regras de cálculo', href: '/tributacao/regra-documento-fiscal', permission: 'Tributação - Regras Cálculo' },
          { name: 'Regra de escrituração', href: '/tributacao/regra-escrituracao', permission: 'Tributação - Regras Cálculo' },
        ],
      },
      {
        name: 'Regras financeiras',
        href: '#',
        icon: Scale,
        permission: ['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo'],
        subItems: [
          { name: 'Regra do título', href: '/tributacao/regra-titulo', permission: ['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo'] },
          { name: 'Regra de cálculo', href: '/tributacao/regra-calculo-financeiro', permission: ['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo'] },
          { name: 'Regra de vencimento', href: '/tributacao/regra-vencimento', permission: ['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo'] },
          { name: 'Regra de retenção', href: '/tributacao/regra-retencao', permission: ['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo'] },
          { name: 'Regra financeira', href: '/tributacao/regra-financeira', permission: ['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo'] },
        ],
      },
      { name: 'Confronto de notas', href: '/tributacao/confronto', icon: FileSearch, permission: ['Tributação - Confronto Fiscal'] },
      { name: 'Perfil fiscal', href: '/tributacao/perfil-fiscal', icon: Building2, permission: ['Tributação - Perfil Fiscal'] },
      { name: 'Diagnóstico Protheus', href: '/tributacao/diagnostico-protheus', icon: Stethoscope, permission: ['Tributação - Diagnóstico Protheus'] },
      {
        name: 'Obrigações acessórias',
        href: '#',
        icon: ClipboardList,
        permission: ['Tributação - SPED'],
        subItems: [
          { name: 'Validação de SPED', href: '/tributacao/sped/validacao', permission: ['Tributação - SPED'] },
          { name: 'Estabelecimentos', href: '/tributacao/sped/estabelecimentos', permission: ['Tributação - SPED'] },
        ],
      },
      { name: 'Tokens API', href: '/tributacao/tokens-api', icon: KeyRound, permission: 'Tributação - Tokens API' },
    ],
  },
];
